/**
 * Normalizes an extracted job description before `react-markdown` parses it.
 *
 * The fetcher cleans these defects at ingest and the content backfill repairs
 * already-stored rows, but this guard keeps a not-yet-backfilled job rendering
 * correctly and hardens the view against future bad data:
 *
 *  1. Literal escape sequences — the two characters `\` + `n` instead of a real
 *     newline, which collapses the description into one unbroken blob.
 *  2. Leaked stylesheet content — `<style>` blocks or CSS-in-JS rulesets
 *     (`.css-1f63e20{display:flex;…}`) that bled in during scraping.
 *  3. Erratic leading indentation — CommonMark treats a line indented 4+ spaces
 *     as a code block, rendering it as oversized, non-wrapping monospace.
 *
 * Mirrors `preprocessMarkdown` in the web app; the two views parse the same
 * column and must agree on what it means.
 */
export function preprocessMarkdown(text: string): string {
  return (
    text
      // Literal escape sequences -> real characters.
      .replace(/\r\n/g, '\n')
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '  ')
      .replace(/\\r/g, '\n')
      // Leaked <style> blocks and CSS-in-JS rulesets. Conservative: the selector
      // must start with a class/id token (`.foo`/`#foo`) — which leaked
      // `.css-<hash>{…}` always does but a Markdown bullet/link/heading won't —
      // and the braces must hold a `;`-terminated declaration, so prose/JSON/code
      // (e.g. `{ status: "ok" }`) is left untouched.
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/[.#][\w-][^{}\n]*\{[^{}]*;[^{}]*\}/g, '')
      // Leading indentation that CommonMark treats as a code block (4+ spaces or
      // a tab); shallower indentation is left so nested lists keep their structure.
      .replace(/^(?:\t+| {4,})/gm, '')
      // Collapse whitespace after a list marker to one space — a bullet/number
      // followed by 5+ spaces ("-        text") renders the item as a code block.
      .replace(/^(\s{0,3}(?:[-*+]|\d+[.)]))[ \t]+/gm, '$1 ')
      // Stray bold markers dangling at line start/end.
      .replace(/\*\*\s*$/gm, '')
      .replace(/^\s*\*\*/gm, '')
  );
}
